import { BootBudget, isBootBudgetSnapshot, type BootBudgetSnapshot } from './boot-watchdog.js';

const STORAGE_KEY = 'studio.boot.budget';
const HOSTED_LOGIN_KEY = 'studio.boot.hosted-login';
/** Seconds-based restart stamp shared with TimeoutWatchdog and main's recovery. */
const RESET_STAMP_KEY = 'iris-reset-at';
let hostedLogin = false;
type RecoveryResult = { accepted: boolean; reason: string };
interface DesktopBoot {
  initial: unknown;
  subscribeHealth?(listener: (ping: { nonce: number; attemptId: string }) => void): () => void;
  command(payload: Record<string, unknown>): { accepted: boolean; snapshot?: BootBudgetSnapshot };
  recover(payload: Record<string, unknown>): Promise<RecoveryResult>;
  subscribe(listener: (snapshot: unknown) => void): () => void;
}
let clock: BootBudget | undefined;
let desktop: DesktopBoot | undefined;
let snapshot: BootBudgetSnapshot | undefined;
let generation = new AbortController();
let recovery: Promise<RecoveryResult> | undefined;
const listeners = new Set<() => void>();
const id = (): string => crypto.randomUUID();
// A closed name set lets CI map each boot event to its PostHog tests.
type BootBudgetEventName = 'boot_started' | 'boot_succeeded' | 'boot_failed';
let capture: (name: BootBudgetEventName, properties: Record<string, unknown>) => void = () => {};
const emit = (
  name: BootBudgetEventName,
  state: BootBudgetSnapshot,
  details: Record<string, unknown> = {}
): void => {
  // Telemetry cannot change accepted state or reject a recovery action.
  try {
    capture(name, {
      ...details,
      attempt_id: state.attemptId,
      attempt_generation: state.generation,
      boot_duration_ms: state.updatedAt - state.startedAt,
      boot_active_ms: state.activeMs,
      boot_user_wait_ms: state.excludedMs,
      last_boot_stage: state.stage
    });
  } catch {
    /* the attempt remains settled */
  }
};
function receive(next: BootBudgetSnapshot): void {
  if (
    snapshot?.attemptId !== next.attemptId ||
    (snapshot?.outcome === null && next.outcome !== null)
  ) {
    generation.abort();
    generation = new AbortController();
    if (next.outcome === 'failed' || next.outcome === 'cancelled') generation.abort();
  }
  snapshot = next;
  for (const listener of listeners) listener();
}
function save(): void {
  if (!clock) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(clock.snapshot()));
  } catch {
    /* memory remains authoritative */
  }
}

/** Called once at application entry, above auth and route-owned lifetimes. */
export function initializeBootBudget(sender: typeof capture): void {
  if (snapshot) return;
  capture = sender;
  desktop = (window as unknown as { iris?: { boot?: DesktopBoot } }).iris?.boot;
  if (desktop) {
    // Tray and auxiliary windows cannot own Studio launch or fall back to a
    // second browser clock when main rejects their attachment.
    if (!isBootBudgetSnapshot(desktop.initial)) {
      desktop = undefined;
      return;
    }
    receive(desktop.initial);
    desktop.subscribe((value) => {
      if (isBootBudgetSnapshot(value)) receive(value);
    });
    return;
  }
  desktop = undefined;
  let restored: unknown;
  try {
    restored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
  } catch {
    /* first launch */
  }
  // Auth0 owns an explicitly requested hosted sign-in surface while Studio's
  // document is absent. Resume machine timing on entry, before callback/token
  // processing. Automatic reloads do not carry this one-shot handoff marker.
  try {
    const humanAttempt = sessionStorage.getItem(HOSTED_LOGIN_KEY);
    sessionStorage.removeItem(HOSTED_LOGIN_KEY);
    if (
      isBootBudgetSnapshot(restored) &&
      restored.outcome === null &&
      humanAttempt === restored.attemptId
    ) {
      const now = Date.now();
      restored = {
        ...restored,
        excludedMs: restored.excludedMs + Math.max(0, now - restored.updatedAt),
        updatedAt: now
      };
    }
  } catch {
    /* unavailable storage keeps the in-document clock authoritative */
  }
  clock = new BootBudget({
    attemptId: id(),
    restored,
    onTerminal: (state) => {
      if (state.outcome === 'failed')
        emit('boot_failed', state, { error_type: state.reason ?? 'unexpected' });
    }
  });
  receive(clock.snapshot());
  clock.subscribe(() => {
    receive(clock!.snapshot());
    save();
  });
  if (!isBootBudgetSnapshot(restored)) emit('boot_started', clock.snapshot());
  // Unload ends the rendered human wait. Time in an automatic document gap is
  // charged on restore; the server/Root route never owns this clock.
  window.addEventListener('pagehide', () => {
    clock?.releaseHumanWaits();
    save();
    generation.abort();
    if (hostedLogin && clock?.snapshot().outcome === null) {
      try {
        sessionStorage.setItem(HOSTED_LOGIN_KEY, clock.snapshot().attemptId);
      } catch {
        /* best effort */
      }
    }
  });
  save();
}
export const getBootSnapshot = (): BootBudgetSnapshot | undefined => snapshot;
/**
 * True when desktop main owns this launch's clock and deadline. The renderer
 * then only reports stages and outcomes; main decides when the budget expires.
 */
export const isLaunchBudgetOwnedByMain = (): boolean => desktop !== undefined;
export const subscribeBootBudget = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const captureBootSignal = (): AbortSignal => generation.signal;

/**
 * Does an aborted launch signal mean the writer that captured it is obsolete?
 *
 * `receive` aborts the generation on the FIRST non-null outcome, so the signal
 * fires for two different things: a newer attempt took over, or this attempt
 * simply finished. Only the first makes a captured writer stale. Aborting at
 * readiness is deliberate -- it stops boot-scoped work from outliving the boot --
 * but a writer still mid-flight when readiness is published is not "later
 * ordinary work", it is the work readiness was waiting on, and retiring it drops
 * whatever it had left to do.
 *
 * Pure + exported for unit-testability, mirroring `bootStallDecision`
 * (boot-overlay/boot-stall.ts). Per rule: reference-rules-in-comments
 * (ai.knowledge/global).
 *
 * @param captured - the attemptId live when the writer captured its signal.
 * @param settled - the snapshot at the moment the signal aborted.
 * @returns `true` when the writer must retire.
 */
export const retiresCapturedWriter = (
  captured: string | undefined,
  settled: BootBudgetSnapshot | undefined
): boolean => !(settled?.attemptId === captured && settled?.outcome === 'succeeded');
function command(payload: Record<string, unknown>): boolean {
  const result = desktop?.command({ ...payload, attemptId: snapshot?.attemptId });
  if (result?.snapshot) receive(result.snapshot);
  return result?.accepted ?? false;
}
export function bindBootAccount(accountId: string | null): void {
  if (!snapshot) return;
  if (desktop) {
    command({ type: 'account', accountId });
    return;
  }
  const prior = clock?.snapshot().attemptId;
  clock?.bindAccount(accountId, id());
  if (clock && clock.snapshot().attemptId !== prior) emit('boot_started', clock.snapshot());
}
export function enterBootHumanWait(): () => void {
  if (!snapshot) return () => {};
  const key = id();
  const attempt = snapshot?.attemptId;
  if (desktop) command({ type: 'wait', key, waiting: true });
  else clock?.humanWait(key, true);
  return () => {
    // A disposer belongs to the attempt that rendered it, not a newer login.
    if (snapshot?.attemptId !== attempt) return;
    if (desktop) command({ type: 'wait', key, waiting: false });
    else clock?.humanWait(key, false);
  };
}
export function setBootStage(stage: string): void {
  if (desktop) command({ type: 'stage', stage });
  else clock?.stage(stage);
}
export function finishBoot(outcome: 'succeeded' | 'failed', reason?: string): boolean {
  if (desktop) return command({ type: 'finish', outcome, reason });
  const accepted = clock?.finish(outcome, reason ?? null) ?? false;
  if (accepted && outcome === 'succeeded' && clock) emit('boot_succeeded', clock.snapshot());
  return accepted;
}
export function recoverBoot(
  kind: 'retry' | 'reload' | 'restart',
  cooldownMs = 60_000
): Promise<RecoveryResult> {
  if (recovery) return recovery;
  if (!snapshot) return Promise.resolve({ accepted: false, reason: 'not-started' });
  // Reserve before invoking desktop IPC or browser persistence. All callers
  // share this action; no error path leaves an unhandled rejection or waiter.
  const attemptId = snapshot.attemptId;
  recovery = Promise.resolve()
    .then(async () => {
      if (snapshot?.attemptId !== attemptId) return { accepted: false, reason: 'obsolete' };
      if (desktop) return desktop.recover({ kind, cooldownMs, attemptId });
      if (
        !clock ||
        (kind !== 'retry' && ['failed', 'cancelled'].includes(clock.snapshot().outcome ?? ''))
      ) {
        return { accepted: false, reason: 'attempt-ended' };
      }
      if (kind === 'restart') {
        // Same stamp and rule as TimeoutWatchdog.shouldAutoReset and main's
        // requestBootRecovery: check and stamp together, so an automatic web
        // restart cannot reload in a loop. An unreadable stamp cannot veto.
        // Per rule: absent-data-gates-permissively (ai.knowledge/global).
        // `#~/persistence/index.js` is loaded HERE, not at module scope. That
        // barrel calls isElectronApp() while evaluating, so a static import
        // drags `#~/electron/utils.js` (and its own chain) into the module
        // graph of every importer of this file. Under jest's ESM loader that
        // turned four unrelated suites red — boot-budget-view, login-progress,
        // register and create-studio.already-existed-handoff — whose mock
        // factories were written against the narrower graph and so omit
        // isElectronApp / isProduction / isNil. Runtime never noticed; only
        // linking did. This branch is the sole consumer, is already async and
        // already tolerates storage being unavailable, so deferring costs
        // nothing. Per knowledge: esm-jest-mock-patterns (ai.knowledge/global).
        let stamp = Number.NaN;
        try {
          const { get } = await import('#~/persistence/index.js');
          stamp = Number(await get(RESET_STAMP_KEY));
        } catch {
          /* storage unavailable: no stamp to honor */
        }
        if (Number.isFinite(stamp) && stamp > 0 && Date.now() / 1000 - stamp <= cooldownMs / 1000) {
          return { accepted: false, reason: 'cooldown' };
        }
        try {
          const { set } = await import('#~/persistence/index.js');
          await set(RESET_STAMP_KEY, String(Date.now() / 1000));
        } catch {
          /* best effort */
        }
        if (snapshot?.attemptId !== attemptId) return { accepted: false, reason: 'obsolete' };
      }
      if (clock.snapshot().outcome === 'failed' || clock.snapshot().outcome === 'succeeded') {
        clock.restart(id());
        emit('boot_started', clock.snapshot());
      }
      clock.releaseHumanWaits();
      save();
      generation.abort();
      window.location.reload();
      return { accepted: true, reason: kind };
    })
    .catch(() => ({ accepted: false, reason: 'recovery-failed' }));
  void recovery.then((result) => {
    if (!result.accepted) recovery = undefined;
  });
  return recovery;
}

/** Mark only an explicit hosted-auth handoff; machine work still runs until pagehide. */
export function markHostedBootWait(): () => void {
  hostedLogin = true;
  return () => {
    hostedLogin = false;
  };
}

/** Mount-owned scheduling/content probe; boot readiness remains the only M1 oracle. */
export function subscribeRendererHealth(): () => void {
  let mounted = true;
  const frames = new Set<number>();
  const detach = desktop?.subscribeHealth?.((ping) => {
    if (!mounted) return;
    const respond = (type = 'renderer-pong'): void => {
      const root = document.getElementById('root');
      if (mounted && root?.isConnected && root.childElementCount > 0) {
        desktop?.command({
          type,
          ...ping,
          visible: document.visibilityState === 'visible'
        });
      }
    };
    // Visibility must rearm an unoccluded window even if its RAF is still stuck.
    respond('renderer-visibility');
    // Hidden documents may legitimately throttle rAF. This reports visibility
    // without asserting paint, usable controls, recovered sources or boot success.
    if (document.visibilityState !== 'visible') respond();
    else {
      const frame = requestAnimationFrame(() => {
        frames.delete(frame);
        respond();
      });
      frames.add(frame);
    }
  });
  return () => {
    mounted = false;
    detach?.();
    for (const frame of frames) cancelAnimationFrame(frame);
    frames.clear();
  };
}
